// SupplierPurchaseOrdersIndent.jsx
import React, { Component } from "react";
import Fuse from "fuse.js";
import CartContext from "./CartContext";
import { MessageBox } from "./MessageBox";
import {
  Navbar,
  Nav,
  Button,
  Row,
  Col,
  Accordion,
  Table,
  FormControl,
  Stack,
  ButtonToolbar,
  Form,
  Badge,
  Spinner,
} from "react-bootstrap";
import { BiRefresh } from "react-icons/bi";

export class SupplierPurchaseOrdersIndent extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: null,
      orders: [],
      filteredOrderKeys: [],
      searchQuery: "",
      orderNoFilter: "",
      lastFetchedAt: null,
      suppliers: [],
      selectedSupplierId: "",
      selectedStatus: "all",
      msgboxOpen: false,
      msgboxTitle: "",
      msgboxMessage: "",
    };
  }

  // helper to determine if current user is a supplier and their supplier id
  getSupplierUserContext() {
    const ctx = this.context || {};
    const user = ctx.userData || {};
    const roles = ctx.UserRoles || {};
    const isSupplierUser =
      user && roles && user.roleId === roles.SUPPLIER ? true : false;
    const supplierUserId = user ? String(user.storeRoleValue || "") : "";
    return { isSupplierUser, supplierUserId };
  }

  componentDidMount() {
    // load suppliers, then if current user is supplier lock to their supplier id, then fetch orders
    this.loadSuppliers()
      .then((list) => {
        const { isSupplierUser, supplierUserId } =
          this.getSupplierUserContext();
        if (isSupplierUser && supplierUserId) {
          // set selectedSupplierId to supplier user's id before fetching orders
          this.setState({ selectedSupplierId: supplierUserId }, () =>
            this.fetchOrders()
          );
        } else {
          this.fetchOrders();
        }
      })
      .catch(() => {
        // even if loadSuppliers fails, still attempt to fetch
        this.fetchOrders();
      });
  }

  // storeId: prop > context
  getStoreId() {
    return this.props.storeId || this.context?.storeId || "";
  }

  loadSuppliers = async () => {
    const storeId = this.getStoreId();
    if (!storeId) return [];
    try {
      const r = await fetch(
        `${process.env.REACT_APP_API}Supplier/GetSuppliers/${storeId}`,
        { credentials: "same-origin" }
      );
      if (!r.ok) throw new Error(`Server returned ${r.status}`);
      const data = await r.json();
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      this.setState({ suppliers: list });
      return list;
    } catch (err) {
      console.error("loadSuppliers error", err);
      this.setState({ suppliers: [] });
      return [];
    }
  };

  buildUrl() {
    const storeId = this.getStoreId();
    if (!storeId) return null;
    const base = process.env.REACT_APP_API || "";
    const { selectedSupplierId } = this.state;
    if (selectedSupplierId && selectedSupplierId.trim() !== "") {
      return `${base}SupplierPurchaseOrders/bysupplier-dto/${storeId}/${selectedSupplierId}`;
    }
    return `${base}SupplierPurchaseOrders/all-dto/${storeId}`;
  }

  fetchOrders = async () => {
    const url = this.buildUrl();
    if (!url) {
      this.ShowMessagBox(
        "Error",
        "storeId is required to load purchase orders."
      );
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const resp = await fetch(url, { credentials: "same-origin" });
      if (!resp.ok) throw new Error(`Server returned ${resp.status}`);
      const data = await resp.json();
      const list = Array.isArray(data) ? data : [];

      const normalized = list.map((pr) => ({
        id: pr.id ?? pr.Id,
        name: pr.name ?? pr.Name,
        purchased: pr.purchased ?? pr.Purchased,
        description: pr.description ?? pr.Description,
        created: pr.created ?? pr.Created,
        storeId: pr.storeId ?? pr.StoreId,
        suppliers: pr.suppliers ?? pr.Suppliers,
        status: pr.status ?? pr.Status ?? 0,
        supplierId: pr.supplierId ?? pr.SupplierId,
        purchaseOrderNo: pr.purchaseOrderNo ?? pr.PurchaseOrderNo ?? "",
        warehouseId: pr.warehouseId ?? pr.WarehouseId,
        createdBy: pr.createdBy ?? pr.CreatedBy,
        items: Array.isArray(pr.items)
          ? pr.items.map((it) => ({
              id: it.id ?? it.Id,
              purchaseRequestId: it.purchaseRequestId ?? it.PurchaseRequestId,
              itemName: it.itemName ?? it.ItemName,
              qty: it.qty ?? it.Qty ?? 0,
              weight: it.weight ?? it.Weight ?? null,
              unit: it.unit ?? it.Unit ?? null,
              purchasePrice: it.purchasePrice ?? it.PurchasePrice ?? null,
              receivedStatus: it.receivedStatus ?? it.ReceivedStatus ?? 0,
              notes: it.notes ?? it.Notes ?? "",
            }))
          : [],
      }));

      normalized.sort((a, b) => {
        const da = a.created ? new Date(a.created).getTime() : 0;
        const db = b.created ? new Date(b.created).getTime() : 0;
        if (da !== db) return db - da;
        return (b.purchaseOrderNo || "").localeCompare(a.purchaseOrderNo || "");
      });

      this.setState(
        { orders: normalized, lastFetchedAt: new Date(), loading: false },
        () => this.applyFilters()
      );
    } catch (err) {
      console.error("fetchOrders error", err);
      this.ShowMessagBox("Error", "Failed to load purchase orders");
      this.setState({ loading: false, error: err.message || String(err) });
    }
  };

  handleRefresh = () => {
    this.fetchOrders();
  };

  handleSearchChange = (e) => {
    const v = e.target.value || "";
    this.setState({ searchQuery: v }, () => this.applyFilters());
  };

  handleOrderNoFilterChange = (e) => {
    const v = e.target.value || "";
    this.setState({ orderNoFilter: v }, () => this.applyFilters());
  };

  handleSupplierFilterChange = (e) => {
    // ignore attempts to change supplier if current user is a supplier
    const { isSupplierUser } = this.getSupplierUserContext();
    if (isSupplierUser) return;
    const val = e.target.value || "";
    this.setState({ selectedSupplierId: val }, () => this.fetchOrders());
  };

  handleStatusTab = (statusKey) => {
    this.setState({ selectedStatus: statusKey }, () => this.applyFilters());
  };

  applyFilters = () => {
    const { orders, selectedStatus, orderNoFilter, searchQuery } = this.state;

    // Start with all orders (filter out status==0 as before)
    let working = (orders || [])
      .slice()
      .filter((o) => Number(o.status ?? 0) !== 0);

    // If logged-in supplier, further restrict to their supplierId (if selectedSupplierId already set it will be applied below)
    const { isSupplierUser, supplierUserId } = this.getSupplierUserContext();
    if (isSupplierUser && supplierUserId) {
      working = working.filter(
        (o) =>
          (o.supplierId || "") &&
          String(o.supplierId).toLowerCase() ===
            String(supplierUserId).toLowerCase()
      );
    }

    // filter by PurchaseRequest.Status (order-level)
    if (selectedStatus && selectedStatus !== "all") {
      const sNum = Number(selectedStatus);
      working = working.filter((o) => Number(o.status ?? 0) === sNum);
    }

    // filter by OrderNo (contains)
    if (orderNoFilter && orderNoFilter.trim() !== "") {
      const q = orderNoFilter.trim().toLowerCase();
      working = working.filter((o) =>
        (o.purchaseOrderNo || "").toLowerCase().includes(q)
      );
    }

    // fuzzy search: search across order-level and item-level
    if (searchQuery && searchQuery.trim() !== "") {
      try {
        const fuseList = working.map((o) => ({
          order: o,
          text:
            (o.purchaseOrderNo || "") +
            " " +
            (o.name || "") +
            " " +
            (o.description || "") +
            " " +
            o.items
              .map((it) => (it.itemName || "") + " " + (it.notes || ""))
              .join(" "),
        }));
        const fuse = new Fuse(fuseList, {
          keys: ["text"],
          threshold: 0.35,
          ignoreLocation: true,
        });
        const res = fuse.search(searchQuery).map((r) => r.item.order);
        working = res;
      } catch {
        const q = searchQuery.trim().toLowerCase();
        working = working.filter((o) => {
          if ((o.purchaseOrderNo || "").toLowerCase().includes(q)) return true;
          if ((o.name || "").toLowerCase().includes(q)) return true;
          if ((o.description || "").toLowerCase().includes(q)) return true;
          for (const it of o.items || []) {
            if ((it.itemName || "").toLowerCase().includes(q)) return true;
            if ((it.notes || "").toLowerCase().includes(q)) return true;
          }
          return false;
        });
      }
    }

    const keys = working.map((o) =>
      String(
        o.id ?? o.purchaseOrderNo ?? o.purchaseOrderNo ?? "__" + Math.random()
      )
    );
    this.setState({ filteredOrderKeys: keys });
  };

  // supplier name resolver
  getSupplierLabel = (supplierId) => {
    if (!supplierId) return "";
    const idStr = String(supplierId).toLowerCase();
    const s = (this.state.suppliers || []).find(
      (x) => (x.id || "").toString().toLowerCase() === idStr
    );
    if (!s) return supplierId;
    return s.name || s.companyName || s.emailId || s.id;
  };

  statusLabel = (status) => {
    switch (Number(status)) {
      case 0:
        return "Required";
      case 1:
        return "Delivered";
      case 2:
        return "Required";
      case 3:
        return "Unavailable";
      default:
        return "Unknown";
    }
  };

  statusCounts = () => {
    const counts = { all: 0, 1: 0, 2: 0, 3: 0, 4: 0 };
    (this.state.orders || []).forEach((o) => {
      const s = Number(o.status ?? 0);
      if (s === 0) return;
      counts.all += 1;
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  };

  ShowMessagBox = (title, message) =>
    this.setState({
      msgboxOpen: true,
      msgboxTitle: title,
      msgboxMessage: message,
    });

  renderAccordionHeader = (order) => {
    const dt = order.created ? new Date(order.created) : null;
    const createdStr = dt
      ? dt.toLocaleDateString() + " " + dt.toLocaleTimeString()
      : "";
    const totalQty = (order.items || []).reduce(
      (s, it) => s + (Number(it.qty) || 0),
      0
    );
    const itemCount = (order.items || []).length;
    const status = Number(order.status ?? 0);

    let badge;
    switch (status) {
      case 0:
        badge = <Badge bg="info">New</Badge>;
        break;
      case 1:
        badge = <Badge bg="primary">Request</Badge>;
        break;
      case 2:
        badge = <Badge bg="success">Delivered</Badge>;
        break;
      case 3:
        badge = <Badge bg="danger">Cancelled</Badge>;
        break;
      default:
        badge = <Badge bg="secondary">Unknown</Badge>;
    }

    return (
      <Row className="w-100 align-items-center">
        <Col md={3}>
          <strong>{order.purchaseOrderNo || "(No OrderNo)"}</strong>
          <div className="small text-muted">{createdStr}</div>
        </Col>
        <Col md={3}>{this.getSupplierLabel(order.supplierId)}</Col>
        <Col md={2}>
          <div className="small">Items: {itemCount}</div>
          <div className="small text-muted">Qty: {totalQty}</div>
        </Col>
        <Col md={4} className="text-end">
          <div>{badge}</div>
          <div className="small text-muted">{order.name || ""}</div>
        </Col>
      </Row>
    );
  };

  renderItemsTable = (items) => {
    return (
      <Table striped bordered hover size="sm" className="mt-2">
        <thead>
          <tr>
            <th>Item</th>
            <th className="text-end">Qty</th>
            <th>Unit</th>
            <th className="text-center">Status</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {(items || []).map((it, idx) => (
            <tr key={it.id ?? idx}>
              <td>{it.itemName}</td>
              <td className="text-end">{it.qty ?? ""}</td>
              <td>{it.unit ?? ""}</td>
              <td className="text-center">
                {this.statusLabel(it.receivedStatus)}
              </td>
              <td
                style={{
                  maxWidth: 300,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <span title={it.notes}>{it.notes}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  render() {
    const {
      orderNoFilter,
      lastFetchedAt,
      loading,
      suppliers,
      selectedSupplierId,
      orders,
      filteredOrderKeys,
      selectedStatus,
      msgboxOpen,
      msgboxTitle,
      msgboxMessage,
    } = this.state;

    const storeId = this.getStoreId();
    const counts = this.statusCounts();
    const { isSupplierUser, supplierUserId } = this.getSupplierUserContext();

    return (
      <>
        {msgboxOpen && (
          <MessageBox
            open={msgboxOpen}
            title={msgboxTitle}
            message={msgboxMessage}
            callback={() => this.setState({ msgboxOpen: false })}
          />
        )}

        <Navbar className="shadow position-sticky fixed-top py-3 text-black">
          <h3>
            <b>Purchase Orders (PO) from Client</b>
          </h3>
          <Navbar.Collapse className="justify-content-end mx-0">
            <Nav>
              <ButtonToolbar>
                <Button
                  size="sm"
                  className="mainthemecolor"
                  onClick={this.handleRefresh}
                  disabled={loading || !storeId}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" /> Refreshing...
                    </>
                  ) : (
                    <>
                      <BiRefresh />
                      &nbsp;Refresh
                    </>
                  )}
                </Button>
              </ButtonToolbar>
            </Nav>
          </Navbar.Collapse>
        </Navbar>

        <div className="p-3" style={{ marginTop: 10 }}>
          <Stack direction="horizontal" gap={3} className="mb-2">
            <FormControl
              style={{ maxWidth: 220 }}
              type="search"
              placeholder="Filter by Order No..."
              value={orderNoFilter}
              onChange={this.handleOrderNoFilterChange}
            />

            <Form.Select
              style={{ maxWidth: 280 }}
              value={selectedSupplierId}
              onChange={this.handleSupplierFilterChange}
              disabled={isSupplierUser}
            >
              <option value="">— All Suppliers —</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.companyName || s.emailId || s.id}
                </option>
              ))}
            </Form.Select>
          </Stack>

          <Nav
            variant="tabs"
            activeKey={selectedStatus}
            onSelect={this.handleStatusTab}
            className="mb-2"
          >
            <Nav.Item>
              <Nav.Link eventKey="all">
                All{" "}
                <Badge bg="secondary" className="ms-1">
                  {counts.all}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="1">
                Request{" "}
                <Badge bg="primary" className="ms-1">
                  {counts[1] ?? 0}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="2">
                Delivered{" "}
                <Badge bg="success" className="ms-1">
                  {counts[2] ?? 0}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="3">
                Partially Delivered{" "}
                <Badge bg="warning" text="dark" className="ms-1">
                  {counts[3] ?? 0}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="4">
                Cancelled{" "}
                <Badge bg="danger" className="ms-1">
                  {counts[4] ?? 0}
                </Badge>
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Accordion
            defaultActiveKey={
              filteredOrderKeys.length ? filteredOrderKeys[0] : null
            }
          >
            {filteredOrderKeys.length === 0 && (
              <div className="text-center text-muted py-4">
                No orders found.
              </div>
            )}

            {filteredOrderKeys.map((key) => {
              const order = orders.find((o) => String(o.id) === String(key));
              if (!order) return null;
              return (
                <Accordion.Item eventKey={key} key={key}>
                  <Accordion.Header>
                    {this.renderAccordionHeader(order)}
                  </Accordion.Header>
                  <Accordion.Body>
                    {this.renderItemsTable(order.items)}
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>

          <div className="mt-2 small text-muted">
            Showing {filteredOrderKeys.length} orders (
            {orders.reduce((s, o) => s + (o.items?.length || 0), 0)} total
            items).
            {lastFetchedAt && (
              <span className="ms-2">
                Last fetched: {new Date(lastFetchedAt).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </>
    );
  }
}

export default SupplierPurchaseOrdersIndent;
